import React, { useState } from "react";
import { Link } from "react-router-dom";
import { LinkedinLogo, XLogo, FacebookLogo, ArrowRight, CheckCircle, EnvelopeSimple } from "@phosphor-icons/react";
import { api } from "../lib/api";

const SOCIALS = [
  { name: "LinkedIn", icon: LinkedinLogo, href: "https://www.linkedin.com/company/fintechomni/" },
  { name: "X", icon: XLogo, href: "https://x.com/fintechomni" },
  { name: "Facebook", icon: FacebookLogo, href: "https://www.facebook.com/fintechomni" },
];

const NewsletterSignup = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/subscribe", { email, source: "footer" });
      setSuccess(true);
      setMessage(res?.data?.message || "You're on the list.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="hard-border bg-cyber-elevated p-5" data-testid="newsletter-success">
        <div className="flex items-center gap-2 text-cyber-green">
          <CheckCircle size={18} weight="duotone" />
          <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em]">/ subscribed</span>
        </div>
        <p className="text-white/80 text-sm mt-2">{message}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="newsletter-form">
      <p className="text-white/60 text-sm leading-relaxed">
        Field notes from the rooms. New events, sponsor briefs, and the occasional take.
      </p>
      <div className="flex flex-col gap-2">
        <div className="relative">
          <EnvelopeSimple size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            data-testid="newsletter-email"
            className="w-full pl-9 pr-3 py-3 bg-cyber-black border border-cyber-line text-white placeholder:text-white/30 font-mono-tech text-xs uppercase tracking-[0.1em] focus:border-cyber-green focus:outline-none"
            disabled={submitting}
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="btn-neon w-full justify-center"
          data-testid="newsletter-submit"
        >
          {submitting ? "..." : "Subscribe"}
          <ArrowRight size={14} weight="bold" />
        </button>
      </div>
      {error && (
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-cyber-red" data-testid="newsletter-error">
          / {error}
        </div>
      )}
    </form>
  );
};

export const SiteFooter = () => {
  return (
    <footer className="border-t border-cyber-line bg-cyber-black mt-24" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-12 grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-4">
          <div className="font-display text-3xl font-black tracking-tighter">FINTECHOMNI<span className="text-cyber-green">.</span></div>
          <p className="text-white/60 mt-3 max-w-md">The events brand built for performance marketers in fintech.</p>
          <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
            {SOCIALS.map((s) => {
              const Icon = s.icon;
              return (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.name}
                  data-testid={`social-${s.name.toLowerCase()}`}
                  className="w-10 h-10 flex items-center justify-center border border-cyber-line text-white hover:border-cyber-green hover:text-cyber-green transition-colors duration-150"
                >
                  <Icon size={16} weight="bold" />
                </a>
              );
            })}
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="term-label mb-3">/ explore</div>
          <ul className="space-y-2 font-mono-tech text-xs uppercase tracking-[0.15em]">
            <li><Link to="/events" className="text-white/70 hover:text-white" data-testid="footer-events">Events</Link></li>
            <li><Link to="/news" className="text-white/70 hover:text-white" data-testid="footer-news">Newsroom</Link></li>
            <li><Link to="/about" className="text-white/70 hover:text-white" data-testid="footer-about">About</Link></li>
            <li><Link to="/contact" className="text-white/70 hover:text-white" data-testid="footer-contact">Contact</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="term-label mb-3">/ contact</div>
          <p className="text-white/70 text-sm">Reach the team via the contact form — sponsorships, speaking, press, partnerships.</p>
          <Link to="/contact" className="inline-block mt-3 font-mono-tech text-xs uppercase tracking-[0.2em] text-white hover:text-cyber-green transition-colors" data-testid="footer-contact-link">Contact form →</Link>
        </div>

        <div className="md:col-span-3">
          <div className="term-label mb-3">/ newsletter</div>
          <NewsletterSignup />
        </div>
      </div>
      <div className="border-t border-cyber-line">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="text-white/40 font-mono-tech text-[11px] uppercase tracking-[0.2em]">© {new Date().getFullYear()} FintechOmni.com</div>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="text-white/50 hover:text-white font-mono-tech text-[11px] uppercase tracking-[0.2em]" data-testid="footer-privacy">Privacy</Link>
            <Link to="/california-privacy" className="text-white/50 hover:text-white font-mono-tech text-[11px] uppercase tracking-[0.2em]" data-testid="footer-ca-privacy">CA Privacy</Link>
            <Link to="/terms" className="text-white/50 hover:text-white font-mono-tech text-[11px] uppercase tracking-[0.2em]" data-testid="footer-terms">Terms</Link>
            <div className="text-white/30 font-mono-tech text-[10px] uppercase tracking-[0.25em] hidden sm:block">v1.0 / build.{new Date().getFullYear()}.0{new Date().getMonth()+1}</div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
